"use client";

import React from "react";
import { CircleCheck, FileCheck, Search, Rocket } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Étape 1 : Diagnostic et étude de faisabilité",
  icon: Search,
  objective: "Analyser la faisabilité de votre projet et définir la stratégie d'enregistrement la plus pertinente.",
  deliverables: ["Analyse de votre projet au regard des critères d'enregistrement de France Compétences", "Évaluation de la pertinence de cibler le RNCP (Répertoire National des Certifications Professionnelles) ou le RS (Répertoire Spécifique)", "Identification des forces de votre projet et des points de vigilance à anticiper", "Proposition d'une stratégie d'enregistrement adaptée à vos ressources et à vos ambitions", "Estimation du calendrier et des moyens nécessaires pour mener à bien le projet"],
  livrables: "Rapport de diagnostic avec recommandations stratégiques et plan d'actions détaillé."
}, {
  title: "Étape 2 : Conception du dossier de création ou de renouvellement",
  icon: FileCheck,
  objective: "Structurer un dossier complet, conforme aux exigences de France Compétences et optimisé pour convaincre les instructeurs et la Commission de la Certification Professionnelle.",
  introAccompagnement: "En co-construction avec vos équipes :",
  deliverables: ["Référentiel d'activités et de compétences : co-construction du référentiel en deux étapes : d'abord le référentiel d'activités (analyse des situations de travail, du contexte d'exercice du métier avec des professionnels), puis le référentiel de compétences. Chaque élément est formalisé en cohérence avec les réalités du métier et les exigences de France Compétences, dans un langage clair et opérationnel.", "Référentiel d'évaluation : conception des modalités et critères d'évaluation qui garantissent la fiabilité, l'équité et la transparence du processus de certification. Chaque modalité est vulgarisée pour être comprise par tous les acteurs : membres des jurys, candidats, formateurs.", "Note de démonstration de l'opportunité et de la valeur d'usage : recherches documentaires et rédaction de l'argumentaire stratégique : données économiques du secteur, données socio-professionnelles du ou des métiers visés, perspectives d'évolution des compétences, besoins du marché du travail, valeur d'usage du projet (insertion, employabilité). Cette note démontre la pertinence de votre certification auprès de France Compétences.", "Note Qualité : rédaction de la note Qualité de votre projet de certification (gestion des jurys, traçabilité, impartialité, réclamations…), dans une logique de transparence et d'éthique."],
  livrables: "Rédaction de l'intégralité du dossier de demande d'enregistrement, en veillant à la cohérence, à la complétude et à la qualité argumentaire de chaque pièce. Dossier complet France Compétences prêt à être déposé sur la plateforme France Compétences, incluant le tableau Promotions ou le tableau Données, la note de démonstration de l'opportunité et de la valeur d'usage du projet de certification, les courriers de témoignages, l'identification et l'analyse des correspondances, les référentiels, la description des moyens techniques, pédagogiques et d'encadrement, les procédures, les conventions de partenariat le cas échéant, le livret VAE et les autres pièces nécessaires."
}, {
  title: "Étape 3 : Déploiement opérationnel de la certification",
  icon: Rocket,
  objective: "Transformer votre certification enregistrée en un dispositif opérationnel, piloté et pérenne.",
  deliverables: ["Accompagnement à la mise en œuvre opérationnelle de votre dispositif certifiant (calendrier, ressources, organisation)", "Formation de vos équipes aux exigences du référentiel de certification et aux procédures de contrôle, en vulgarisant les concepts techniques", "Structuration du pilotage de votre certification (indicateurs, tableaux de bord, reporting)", "Conseil sur l'amélioration continue de votre dispositif certifiant dans la durée", "Suivi post-enregistrement pour sécuriser les premières sessions d'examen"],
  livrables: "Plan de déploiement opérationnel, outils de pilotage et formation des équipes."
}];
function StepCard({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="overflow-hidden rounded-xl border border-[#d6cfc4]/60 bg-[#F5F0EB] shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
        <CardContent className="p-8 lg:p-10">
          {/* Titre avec icône */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-[#62751D]/10 text-[#62751D] shrink-0">
              <step.icon className="w-7 h-7" />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-foreground">
              {step.title}
            </h3>
          </div>

          {/* Objectif */}
          <div className="mb-6">
            <p className="text-lg font-bold text-gray-800 mb-1">Objectif</p>
            <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)]">
              {step.objective}
            </p>
          </div>

          {/* L'accompagnement */}
          <div className="mb-6">
            <p className="text-lg font-bold text-gray-800 mb-3">
              L'accompagnement
            </p>
            {step.introAccompagnement && <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)] mb-3 italic">
                {step.introAccompagnement}
              </p>}
            <ul className="space-y-4">
              {step.deliverables.map((d, i) => <li key={i} className="flex items-start gap-3">
                  <CircleCheck className="w-5 h-5 text-[#62751D] shrink-0 mt-1" />
                  <span className="text-base leading-relaxed !text-[hsl(210,10%,23%)]">
                    {d}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Livrables */}
          <div>
            <p className="text-lg font-bold text-gray-800 mb-1">Livrables</p>
            <p className="text-base leading-relaxed !text-[hsl(210,10%,23%)] font-semibold">
              {step.livrables}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function MonAccompagnement() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="mon-accompagnement" className="bg-background relative overflow-hidden ![padding:60px_0px_128px_0px]">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-[#62751D]/5 blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full bg-accent/10 blur-[120px]" />
      </div>

      <div className="container max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl font-bold mb-6 !text-[hsl(210,10%,23%)] lg:text-5xl">
            Un accompagnement complet, co-construit à chaque étape
          </h2>
          <p className="text-lg leading-relaxed !text-[hsl(210,10%,23%)]">
            EFFET CONSEIL accompagne chaque étape de votre projet de
            certification professionnelle, qu'il s'agisse d'une création ex
            nihilo ou du renouvellement d'une certification existante. La
            méthode repose sur la co-construction avec vos équipes : votre
            expertise métier est au cœur du processus.
          </p>
        </div>

        {/* Steps */}
        <div className="flex flex-col gap-8">
          {steps.map((s, i) => <StepCard key={i} index={i} step={s} />)}
        </div>
      </div>
    </section>;
}